import {
  GAD7,
  MedicareAccessBHSymptomDiary,
  PHQ4SymptomName,
  PHQ9,
  SymptomDiaryStage,
  UserData,
} from 'hydra-data-types';
import {
  enumValues,
  isNonEmpty,
  objectEntries,
  objectFromEntries,
  omit,
  pick,
  stripUndefined,
} from 'ts-utils';
import { GAD7ScreenName, MedicareAccessScreenName, PHQ9ScreenName } from '../config/FlowTypes';
import { MedicareAccessUserData, UserDataState } from '../store/userData/UserDataState';

/**
 * The Medicare Access PROMs stage the user is completing. Hard-coded to Initial until WA tracks the
 * Medicare lifecycle stage and can resolve the recurring check-ins.
 * TODO(KID-90): derive from the user's Medicare lifecycle status (q1/q2/q3/final).
 */
export function getMedicareAccessStage(): SymptomDiaryStage {
  return SymptomDiaryStage.Initial;
}

// Phq9HarmOthers is a safety-screening question, not a scored PHQ-9 item, so it is excluded from
// the PROMs symptom diary and the proms-complete count.
const PHQ9_PROMS_SCREEN_NAMES = enumValues(PHQ9ScreenName).filter(
  (screenName) => screenName !== PHQ9ScreenName.Phq9HarmOthers
);

/** Whether every GAD7 and PHQ9 PROMs answer has been collected. */
export function isMedicareAccessPromsComplete(
  symptoms: Partial<MedicareAccessBHSymptomDiary>
): boolean {
  return (
    Object.keys(symptoms.gad7 ?? {}).length === enumValues(GAD7ScreenName).length &&
    Object.keys(symptoms.phq9 ?? {}).length === PHQ9_PROMS_SCREEN_NAMES.length
  );
}

/** Minimum total PHQ-9 or GAD-7 score for the Medicare Access severity criterion. */
const MEDICARE_ACCESS_SEVERITY_THRESHOLD = 10;

/** The "no exclusions apply" answer value on the Medicare Access exclusion-criteria screen. */
const NONE_OF_THE_ABOVE = 'noneOfTheAbove';

const GAD7_SCREEN_NAMES = enumValues(GAD7ScreenName);

function promScore(value: string[] | undefined): number {
  const answer = value?.first();
  return answer === undefined ? 0 : Number(answer);
}

function totalPromScore(
  userData: UserDataState,
  screenNames: Array<GAD7ScreenName | PHQ9ScreenName>
): number {
  return screenNames.reduce((total, screenName) => total + promScore(userData[screenName]), 0);
}

/**
 * Whether the user meets the three D2C Medicare Access clinical-eligibility criteria:
 *  1. severity — total PHQ-9 OR total GAD-7 is 10 or more;
 *  2. risk — neither the suicidal- nor homicidal-ideation confirmation is "Yes";
 *  3. exclusions — the exclusion-criteria screen is "none of the above".
 *
 * The eligibility API assigns the control-vs-eligibility arm; this decides eligible vs disqualified
 * within the eligibility arm (the hybrid model).
 */
export function isMedicareAccessClinicallyEligible(userData: UserDataState = {}): boolean {
  const meetsSeverity =
    totalPromScore(userData, GAD7_SCREEN_NAMES) >= MEDICARE_ACCESS_SEVERITY_THRESHOLD ||
    totalPromScore(userData, PHQ9_PROMS_SCREEN_NAMES) >= MEDICARE_ACCESS_SEVERITY_THRESHOLD;

  // The suicide/harm-others confirmation screens and the exclusion screen use MedicareAccess screen
  // names, which aren't part of the typed userData keys — mirror the cast used elsewhere.
  const answers = userData as Partial<Record<MedicareAccessScreenName, string[]>>;
  const hasNoConfirmedRisk =
    answers[MedicareAccessScreenName.MedicareAccessSuicideCheck]?.first() !== 'Yes' &&
    answers[MedicareAccessScreenName.MedicareAccessHarmOthersCheck]?.first() !== 'Yes';

  const exclusionAnswers = answers[MedicareAccessScreenName.MedicareAccessExclusion] ?? [];
  const hasNoExclusions =
    exclusionAnswers.length > 0 && exclusionAnswers.every((value) => value === NONE_OF_THE_ABOVE);

  return meetsSeverity && hasNoConfirmedRisk && hasNoExclusions;
}

export type MedicareAccessUserKeys = 'legalGivenName' | 'legalFamilyName' | 'medicareBeneficiaryId';

export type MedicareAccessUserUpdate = Pick<MedicareAccessUserData, MedicareAccessUserKeys>;

export type SplitMedicareAccessUserData = {
  userData: UserData;
  medicareAccessBHSymptom: Partial<MedicareAccessBHSymptomDiary>;
  medicareAccessUser: MedicareAccessUserUpdate;
};

const medicareAccessInitialSymptomKeys: Array<GAD7ScreenName | PHQ9ScreenName> = [
  ...enumValues(GAD7ScreenName),
  ...PHQ9_PROMS_SCREEN_NAMES,
];

const medicareAccessUserKeys: MedicareAccessUserKeys[] = [
  'legalGivenName',
  'legalFamilyName',
  'medicareBeneficiaryId',
];

// PHQ-4 is GAD-2 + PHQ-2 — the first two GAD-7 and first two PHQ-9 Medicare PROMs questions. We copy
// those answers into user_data.initialSymptoms so the mobile app pre-fills its baseline PHQ-4. The
// 0-3 scale and questions are identical, so the only conversion is the stored string → number.
const MEDICARE_ACCESS_PHQ4_SOURCES: Array<[PHQ4SymptomName, GAD7ScreenName | PHQ9ScreenName]> = [
  ['anxiety', GAD7ScreenName.Gad7Nervous],
  ['worry', GAD7ScreenName.Gad7StopWorrying],
  ['pleasure', PHQ9ScreenName.Phq9LittleInterest],
  ['depression', PHQ9ScreenName.Phq9FeelingDown],
];

/** Build the PHQ-4 subset (`initialSymptoms`) from the Medicare PROMs answers that exist so far. */
function getMedicareAccessInitialSymptoms(
  userDataState: UserDataState
): Partial<Record<PHQ4SymptomName, number>> {
  return stripUndefined(
    objectFromEntries(
      MEDICARE_ACCESS_PHQ4_SOURCES.map(([phq4Key, screenName]) => [
        phq4Key,
        optionalValueToNumber(userDataState[screenName]),
      ])
    )
  );
}

/** Split store userData into Firestore targets: `user_data`, symptom diary, and user doc. */
export function splitOutMedicareAccessInitialSymptoms(
  userDataState: UserDataState
): SplitMedicareAccessUserData {
  const strippedUserData = stripMedicareAccessFromUserData(userDataState);
  const initialSymptoms = getMedicareAccessInitialSymptoms(userDataState);
  return {
    // The PROMs answers themselves are stripped to the symptom diary, but the PHQ-4 subset stays in
    // user_data as initialSymptoms for mobile pre-fill. Both stores deep-merge it, so partial is safe.
    userData: isNonEmpty(initialSymptoms)
      ? ({ ...strippedUserData, initialSymptoms } as UserData)
      : strippedUserData,
    medicareAccessBHSymptom: stripUndefined({
      gad7: getFilteredSymptoms(userDataState, 'gad7') as GAD7,
      phq9: getFilteredSymptoms(userDataState, 'phq9') as PHQ9,
    }),
    medicareAccessUser: getMedicareAccessUser(userDataState),
  };
}

/** Strip Medicare Access fields that are stored outside `user_data`. */
export function stripMedicareAccessFromUserData(userDataState: UserDataState = {}): UserData {
  return omit(userDataState, [
    ...medicareAccessInitialSymptomKeys,
    ...medicareAccessUserKeys,
  ]) as UserData;
}

/** Get the Medicare Access user data from the userDataState. */
export function getMedicareAccessUser(userDataState: UserDataState = {}): MedicareAccessUserUpdate {
  return pick(userDataState, medicareAccessUserKeys) as MedicareAccessUserUpdate;
}

/** Get the symptoms from the userDataState that start with the given prefix
 * and drops the prefix from the key. */
function getFilteredSymptoms(userDataState: UserDataState, prefix: string): Record<string, number> {
  return objectFromEntries(
    objectEntries(pick(userDataState, medicareAccessInitialSymptomKeys))
      .filter(([key]) => key.startsWith(prefix))
      .map(([key, value]) => [key.replace(prefix + '.', ''), valueToNumber(value)])
  );
}

/** Convert the userData value to a number. */
function valueToNumber(value: string[] | undefined): number {
  return Number(value?.first());
}

/** Like valueToNumber, but returns undefined (rather than NaN) when the answer is missing. */
function optionalValueToNumber(value: string[] | undefined): number | undefined {
  const answer = value?.first();
  return answer === undefined ? undefined : Number(answer);
}
